import type { UseFormReturnType } from "@mantine/form";
import * as Sentry from "@sentry/react";
import { UserActivationStatus, WhiteboardPermission } from "@src/gql/graphql";
import {
	showErrorNotification,
	showSuccessNotification,
} from "@src/utils/notifications";
import type { z } from "zod";
import type { GrantEntityPermission } from "./permission-updates";
import type {
	Role,
	inviteFormSchema,
	userSelected,
} from "./whiteboard-invite-schema";

/**
 * Process email input and create new user entries with the selected role
 */
export function processEmailInput(
	emailsString: string,
	selectedRole: Role,
): z.infer<typeof userSelected>[] {
	const newEmails = emailsString
		.split(",")
		.map((email) => email.trim())
		.filter((email) => email);

	return newEmails.map((email) => ({
		email,
		name: "",
		avatarUrl: "",
		activationStatus: UserActivationStatus.Invited,
		permission: selectedRole,
	}));
}

/**
 * Organize users with owner first, followed by other existing users and new users.
 * Also validates against quota limits.
 */
export function organizeUsers(
	existingUsers: z.infer<typeof userSelected>[],
	newUsers: z.infer<typeof userSelected>[],
	currentUserEmail: string | undefined,
	quota?: number,
): {
	sortedUsers: z.infer<typeof userSelected>[];
	quotaExceeded: boolean;
} {
	let currentOwner: z.infer<typeof userSelected> | undefined = undefined;
	const otherSharedUsers: z.infer<typeof userSelected>[] = [];

	for (const user of existingUsers) {
		if (user.email === currentUserEmail) {
			currentOwner = user;
		} else {
			otherSharedUsers.push(user);
		}
	}

	const sortedUsers = [
		...(currentOwner ? [currentOwner] : []),
		...otherSharedUsers,
		...newUsers,
	];

	// Editor seats only — viewers do not consume AddBoardEditors quota.
	// Owner is excluded (current user is not part of the seat count).
	const editorCount = sortedUsers.filter(
		(user) => user.permission === "Editor",
	).length;
	const quotaExceeded = Boolean(quota && editorCount > quota);

	return { sortedUsers, quotaExceeded };
}

/**
 * Checks if the editor-seat quota is exceeded and sets an error message if it is
 * @returns true if quota is exceeded, false otherwise
 */
export function checkQuotaExceeded(
	quotaExceeded: boolean,
	quota: number,
	form: UseFormReturnType<z.infer<typeof inviteFormSchema>>,
): boolean {
	if (quotaExceeded) {
		form.setFieldError(
			"emails",
			`You have reached the maximum of ${quota} collaborator${
				quota === 1 ? "" : "s"
			}. Upgrade to Pro to invite more users.`,
		);
		return true;
	}
	return false;
}

/**
 * Process new users and grant them permissions on the shared entity
 */
export async function processNewUsers(
	newUsers: z.infer<typeof userSelected>[],
	entityId: string,
	selectedRole: Role,
	grantPermission: GrantEntityPermission,
	form: UseFormReturnType<z.infer<typeof inviteFormSchema>>,
	onSuccess: () => void,
	entityLabel = "whiteboard",
): Promise<boolean> {
	if (newUsers.length === 0) {
		return true;
	}

	form.setFieldValue("emails", "");

	try {
		await grantPermission({
			entityId,
			userEmails: newUsers.map((user) => user.email),
			permission:
				selectedRole === "Viewer"
					? WhiteboardPermission.Read
					: WhiteboardPermission.Write,
		});

		showSuccessNotification("Success", `User added to ${entityLabel}`);
		onSuccess();
		return true;
	} catch (error) {
		Sentry.captureException(error);
		showErrorNotification("Error", `Failed to add user to ${entityLabel}`);
		return false;
	}
}
